import React, { useCallback, useEffect, useState } from 'react';
import { Button, Card, Empty, Popconfirm, Segmented, Select, Space, Table, Tag, Tooltip, Typography, message } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import PageHeader from '../../../shared/components/PageHeader';
import evmAdminService from '../../../shared/services/evmAdminService';
import { useTranslation } from '../../../shared/i18n';
import { formatMoney, PLATFORM_CURRENCY } from '../../../shared/utils/money';

const { Text } = Typography;
const ok = (r) => r && r.status === 'success';
const STATUS = { pending: ['待开通', 'processing'], provisioned: ['已开通', 'success'], cancelled: ['已取消', 'default'] };
const asList = (d) => (Array.isArray(d) ? d : (d?.data || d?.nodes || d?.items || []));

/** Admin: paid EVM/VDS orders and upgrades waiting for capacity. */
const PendingProvisions = () => {
    const { t } = useTranslation();
    const [status, setStatus] = useState('pending');
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [nodes, setNodes] = useState([]);
    const [pick, setPick] = useState({}); // item id → node id
    const [acting, setActing] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        const r = await evmAdminService.getPendingProvisions(status);
        setLoading(false);
        setData(ok(r) ? r.data : { available: true, items: [], elastic: [] });
    }, [status]);
    useEffect(() => { load(); }, [load]);
    useEffect(() => {
        evmAdminService.getNodeList({ per_page: 200 }).then((r) => setNodes(ok(r) ? asList(r.data) : []));
    }, []);

    const act = async (key, p) => {
        setActing(key);
        const r = await p;
        setActing(null);
        if (ok(r)) { message.success(r.msg || t('Submitted')); load(); } else message.error(r?.msg || r?.error || t('Operation failed'));
    };

    const columns = [
        { title: 'ID', dataIndex: 'id', width: 60 },
        { title: t('User'), dataIndex: 'user_id', width: 80, render: (v) => `#${v}` },
        { title: t('Product'), dataIndex: 'product', render: (v, r) => <Space direction="vertical" size={0}><span>{v || `#${r.product_id}`}</span><Text type="secondary" style={{ fontSize: 12 }}>{`${r.cores}${t('Cores')} ${+(Number(r.memory || 0) / 1024).toFixed(1)}G`}</Text></Space> },
        { title: t('Amount'), dataIndex: 'amount', width: 90, render: (v, r) => <Tooltip title={`${t('Invoices')} #${r.invoice_id}`}>{formatMoney(Number(v || 0), r.currency || PLATFORM_CURRENCY)}</Tooltip> },
        { title: t('Status'), dataIndex: 'status', width: 90, render: (v, r) => <Tag color={STATUS[v]?.[1]}>{t(STATUS[v]?.[0] || v)}{r.vm_id ? ` #${r.vm_id}` : ''}</Tag> },
        {
            title: t('Reason'), key: 'reasons', render: (_, r) => {
                const rs = r.reasons || [];
                if (!rs.length) return '-';
                const name = (id) => nodes.find((n) => n.id === id)?.name || `#${id}`;
                return (
                    <Tooltip title={<div>{rs.map((x, i) => <div key={i}>{name(x.node_id)}: {x.reason}</div>)}</div>}>
                        <Text type="secondary" style={{ fontSize: 12 }} ellipsis>{`${t('{{n}} tries', { n: r.attempts })} · ${name(rs[0].node_id)}: ${rs[0].reason}`}</Text>
                    </Tooltip>
                );
            },
        },
        { title: t('Created at'), dataIndex: 'created_at', width: 160, render: (v, r) => <Tooltip title={r.last_try_at ? `${t('Last attempt')} ${r.last_try_at}` : null}>{v}</Tooltip> },
        {
            title: t('Actions'), key: 'op', width: 330, render: (_, r) => (r.status !== 'pending' ? null : (
                <Space size={4} wrap>
                    <Button size="small" loading={acting === `r${r.id}`} onClick={() => act(`r${r.id}`, evmAdminService.retryPendingProvision(r.id))}>{t('Retry now')}</Button>
                    <Space.Compact size="small">
                        <Select size="small" allowClear showSearch optionFilterProp="label" placeholder={t('Pick node')} style={{ width: 120 }}
                            value={pick[r.id]} onChange={(v) => setPick((s) => ({ ...s, [r.id]: v }))}
                            options={nodes.map((n) => ({ value: n.id, label: n.name }))} />
                        <Button size="small" disabled={!pick[r.id]} loading={acting === `n${r.id}`} onClick={() => act(`n${r.id}`, evmAdminService.retryPendingProvision(r.id, pick[r.id]))}>{t('Enable')}</Button>
                    </Space.Compact>
                    <Popconfirm title={t('Cancel and refund?')} okButtonProps={{ danger: true }} onConfirm={() => act(`c${r.id}`, evmAdminService.cancelPendingProvision(r.id))}>
                        <Button size="small" danger loading={acting === `c${r.id}`}>{t('Cancel & refund')}</Button>
                    </Popconfirm>
                </Space>
            )),
        },
    ];

    const elasticCols = [
        { title: t('Instances'), dataIndex: 'vm_id', width: 80, render: (v) => `#${v}` },
        { title: t('Resources'), dataIndex: 'resource', render: (v, r) => `${v}: ${r.from} → ${r.target}` },
        { title: t('Amount'), dataIndex: 'amount', width: 90, render: (v, r) => <Tooltip title={`${t('Invoices')} #${r.invoice_id}`}>{formatMoney(Number(v || 0), r.currency || PLATFORM_CURRENCY)}</Tooltip> },
        { title: t('Reason'), dataIndex: 'reason', render: (v) => <Text type="secondary" style={{ fontSize: 12 }}>{v || '-'}</Text> },
        { title: t('Waiting since'), dataIndex: 'since', width: 160 },
        {
            title: '', key: 'op', width: 90, render: (_, r) => (
                <Popconfirm title={t('Refund?')} okButtonProps={{ danger: true }} onConfirm={() => act(`e${r.item_id}`, evmAdminService.cancelPendingElastic(r.item_id))}>
                    <Button size="small" danger loading={acting === `e${r.item_id}`}>{t('Refund')}</Button>
                </Popconfirm>
            ),
        },
    ];

    return (
        <div>
            <PageHeader title={t('Pending provisioning')} extra={<Button icon={<ReloadOutlined />} onClick={load} loading={loading} />} />
            {data && !data.available ? <Empty description={t('Database not migrated')} /> : (
                <Space direction="vertical" size={12} style={{ width: '100%' }}>
                    <Segmented value={status} onChange={setStatus} options={['pending', 'provisioned', 'cancelled', 'all'].map((v) => ({ value: v, label: v === 'all' ? t('All') : t(STATUS[v][0]) }))} />
                    <Table size="small" rowKey="id" loading={loading} dataSource={data?.items || []} columns={columns} pagination={{ pageSize: 50, hideOnSinglePage: true }} scroll={{ x: 1100 }} />
                    {data?.elastic?.length > 0 && (
                        <Card size="small" title={t('Pending upgrades')} extra={
                            <Button size="small" loading={acting === 'er'} onClick={() => act('er', evmAdminService.retryPendingElastic())}>{t('Retry')}</Button>
                        }>
                            <Table size="small" rowKey="item_id" dataSource={data.elastic} columns={elasticCols} pagination={false} scroll={{ x: 800 }} />
                        </Card>
                    )}
                </Space>
            )}
        </div>
    );
};

export default PendingProvisions;
